// @ts-nocheck
/**
 * SemanticValidator.ts — WP-008 Semantic Validator
 *
 * Layer 2 of the Template V2 validation pipeline.
 * Assumes structural validity (calls WP-007 first) and then checks
 * cross-reference integrity:
 *
 *   A. metadata.primaryElementId → element must exist, or `'default'` generic sentinel
 *   B. element.categoryIds → each must reference a known category
 *   C. property.categoryId → must back-reference a known category
 *   D. DataRef resolution (categoryProperty, computed, runtimeContext)
 *   E. ComponentActionRef resolution (onPress, onLongPress, button.action)
 *   F. ScriptInvocation resolution (event bindings, computed values)
 *   G. Expression ID resolution (ValueSource/ValueOrExpression kind='expression')
 *   H. Manifest AssetRef resolution (image.source kind='manifest')
 *   I. Duplicate component IDs within an element across layouts
 *
 * Does NOT perform structural validation. Does not throw. Returns ValidationResult.
 *
 * Pure TypeScript — no framework dependencies.
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue, makeFailResult, makePassResult, mergeResults } from '../../diagnostics';
import type {
    AnyScriptDefinition,
    BlocklyScriptDefinition,
    ComponentNode,
    DataRef,
    ElementDefinition,
    ExpressionDefinition,
    ScriptEventBinding,
    TemplateClassDefinition,
    TemplateDocument,
} from '../../types/canonical';
import {
  bindingRefFromValueSource,
  dataRefsEqual,
  getDisplayValueSource,
  hasNestedButtonLabelSource,
  isComponentEditable,
} from '../../runtime/binding/bindingContract';
import { validatePressHandlerReference } from './pressHandlerValidation';
import { normalizeEventHandler } from '../../types/eventHandler';
import { isClassScriptEventName, isClassVariableDefaultType } from '../../types/templateClass';
import { resolveClassAncestry } from '../../runtime/loader/flattenTemplateClasses';
import { validateStructure } from '../structural';
import { walkComponents } from './componentWalker';
import type { SemanticLookups } from './lookups';
import { buildSemanticLookups } from './lookups';
import {
  detectFieldBindingCycles,
} from '../../runtime/pipeline/fieldBindingValidation';
import { collectItemMetricRefsFromAstRoot } from '../../runtime/metrics/collectItemMetricRefs';
import { collectItemVariableKeysFromAstRoot } from '../../runtime/variables/collectItemVariableRefs';
import { ITEM_VARIABLE_POLICY } from '../../runtime/variables/itemVariablePolicy';
import { LOCAL_STATE_POLICY } from '../../runtime/localState/localStatePolicy';
import { validateLocalStateKey } from '../../runtime/localState/LocalStateSessionStore';
import { detectExpressionCycles } from '../../runtime/expressions/expressionDependencyValidation';
import { validateAnimationDefinitions } from './animationSemanticValidation';
import { checkElementEditForms } from './editFormValidation';
import { checkElementOverview } from './overviewValidation';
import {
  ASYNC_ITEM_METRIC_KEYS,
  SYNC_ITEM_METRIC_KEYS,
} from '../../runtime/metrics/types';
import type { ItemMetricKey, ThemeColorKey } from '../../types/binding';
import { THEME_COLOR_KEYS, isItemMembershipRef } from '../../types/binding';

// ── Runtime context keys ──────────────────────────────────────────────────────

const RUNTIME_CONTEXT_KEYS = new Set<string>([
  'currentDateTime',
  'currentUserId',
  'privacyLevel',
  'authState',
  'canvasScale',
  'canvasMode',
  'colorScheme',
]);

const ITEM_METRIC_KEYS = new Set<ItemMetricKey>([
  ...SYNC_ITEM_METRIC_KEYS,
  ...ASYNC_ITEM_METRIC_KEYS,
]);

const THEME_COLOR_KEY_SET = new Set<ThemeColorKey>(THEME_COLOR_KEYS);

// ── Blockly capability map (WP-041) ──────────────────────────────────────────────────

/**
 * Maps callHost() method names to the ScriptCapabilityRef.capability values they require.
 * Mirrors CapabilityPolicy.CAPABILITY_MAP in the runtime layer but lives here to avoid
 * cross-layer imports from the validator into the runtime.
 */
const BLOCKLY_METHOD_CAPABILITIES: Readonly<Record<string, readonly string[]>> = {
  showAlert:           ['dialog.open'],
  showToast:           ['dialog.open'],
  showConfirm:         ['dialog.open'],
  showChoice:          ['dialog.open'],
  showTextInput:       ['dialog.open'],
  showColorPicker:     ['dialog.open'],
  showDateTimePicker:  ['dialog.open'],
  showSvgDialog:       ['dialog.open'],
  openEditForm:        ['dialog.open'],
  openEditEntry:       ['dialog.open'],
  dispatchBuiltin:     ['scripts.invoke'],
  navigate:            ['navigation.open'],
  openLink:            ['navigation.open'],
  openWebsite:         ['navigation.open'],
  openDeepLink:        ['navigation.open'],
  openSearch:          ['navigation.open'],
  getCurrentDateTime:  [],
  applyMutations:      ['write.item'],
  getItemData:         ['read.item'],
  getItemMetric:       ['read.item'],
  resolveLayoutIndex:  ['read.item'],
  isLayoutEnabled:     ['read.item'],
  setLayoutEnabled:    ['write.item'],
  getItemElementId:    ['read.item'],
  itemHasElement:      ['read.item'],
  listTemplateElementIds: ['read.item'],
  listElementLayoutIds: ['read.item'],
  resolveLayoutId:     ['read.item'],
  getElementDisplayName: ['read.item'],
  getLayoutDisplayName: ['read.item'],
  setItemData:         ['write.item'],
  appendTableRow:      ['write.item'],
  setTableRow:         ['write.item'],
  setTableColumn:      ['write.item'],
  removeTableRow:      ['write.item'],
  clearTable:          ['write.item'],
  setTableRows:        ['write.item'],
  getTableRowCount:    ['read.item'],
  getTableColumns:     ['read.item'],
  getTableMeta:        ['read.item'],
  getTableRows:        ['read.item'],
  tableToCsv:          ['read.item'],
  getTableRow:         ['read.item'],
  createEmptyTableRow: ['read.item'],
  extractTableColumn:  ['read.item'],
  listToCsv:           [],
  sanitizeFilename:    [],
  getItemVariable:     ['read.item'],
  setItemVariable:     ['write.item'],
  deleteItemVariable:  ['write.item'],
  getLocalState:       ['read.item'],
  setLocalState:       ['write.item'],
  deleteLocalState:    ['write.item'],
  getParentData:       ['read.relatives'],
  getChildData:        ['read.relatives'],
  getSiblingData:      ['read.relatives'],
  listRootItemRefs:    ['read.relatives'],
  getRootItemRef:      ['read.relatives'],
  getParentItemRef:    ['read.relatives'],
  listChildItemRefs:   ['read.relatives'],
  listSiblingItemRefs: ['read.relatives'],
  listOutgoingItemRefs: ['read.graph'],
  listIncomingItemRefs: ['read.graph'],
  listGraphEdges: ['read.graph'],
  isPlaceholderItem:   ['read.item'],
  itemExists:          ['read.item'],
  getOriginalItemRef:  ['read.item'],
  listPlaceholderItemRefs: ['read.relatives'],
  getItemRefByUuid:    ['read.relatives'],
  getItemRefsByUuids:  ['read.relatives'],
  getItemUuid:         ['read.item'],
  ensureInternalProductCode: ['write.item'],
  evaluateExpression:  ['scripts.invoke'],
  getAiCredits:        ['ai.readBudget'],
  scheduleReminder:    ['notifications.write'],
  listItemReminders:   ['notifications.read'],
  getReminder:         ['notifications.read'],
  updateReminder:      ['notifications.write'],
  cancelReminder:      ['notifications.write'],
  startCountdownTimer: ['notifications.write'],
  startTimer: ['write.item'],
  getTimer: ['read.item'],
  listItemTimers: ['read.item'],
  itemHasTimer: ['read.item'],
  getReminderIdForTimer: ['read.item'],
  getTimerIdForReminder: ['read.item'],
  pauseTimer: ['write.item'],
  resumeTimer: ['write.item'],
  stopTimer: ['write.item'],
  lapStopwatch: ['write.item'],
  openItemReminder: ['notifications.write'],
  setItemStyleToolbar: [],
  applyCanvasBackground: ['write.item'],
  applyCanvasPattern: ['write.item'],
  dispatchAction:      ['scripts.invoke'],
  listItemScripts:     ['read.item'],
  listItemScriptsByPrefix: ['read.item'],
  listItemClasses:     ['read.item'],
  itemHasClass:        ['read.item'],
  listItemPropertiesByType: ['read.item'],
  listEnumOptions:     [],
  runItemScript:       ['scripts.invoke'],
  openBarcodeScanner:  ['barcodeScanner.open'],
  openStatistics:      ['statistics.open'],
  computePlot:         ['statistics.compute'],
  showPlot:            ['statistics.open'],
  listSum:             ['statistics.compute'],
  listMean:            ['statistics.compute'],
  listMedian:          ['statistics.compute'],
  listStdDev:          ['statistics.compute'],
  listRange:           ['statistics.compute'],
  listMin:             ['statistics.compute'],
  listMax:             ['statistics.compute'],
  listAsort:           ['statistics.compute'],
  listUnique:          ['statistics.compute'],
  listFiveNumberSummary: ['statistics.compute'],
  listCorrelation:     ['statistics.compute'],
  listCrossCorrelation:['statistics.compute'],
  listInterpolate:     ['statistics.compute'],
  listOverlapRange:    ['statistics.compute'],
  listCorrelateSeries: ['statistics.compute'],
  listTTest:           ['statistics.compute'],
  listTTestPaired:     ['statistics.compute'],
  listTTestOneSample:  ['statistics.compute'],
  listLinearRegression:['statistics.compute'],
  listCumulativeSum:   ['statistics.compute'],
  listMovingAverage:   ['statistics.compute'],
  listConvolve:        ['statistics.compute'],
  listReverse:         ['statistics.compute'],
  listTranspose:       ['statistics.compute'],
  listDft:             ['statistics.compute'],
  listFft:             ['statistics.compute'],
  listIdft:            ['statistics.compute'],
  listIfft:            ['statistics.compute'],
  listAvailableDataSources: ['statistics.compute'],
  getDataSeries:       ['statistics.compute'],
  getDataSource:       ['statistics.compute'],
  readClipboardText:   ['clipboard.read'],
  createItem:          ['write.item'],
  createChildItem:     ['write.relatives'],
  createItemExtended:  ['write.item'],
  startQuickAdd:       ['write.item', 'write.relatives', 'images.write'],
  startFastCapture:    ['write.item', 'write.relatives', 'images.write'],
  moveItemToTrash:     ['write.item'],
  importImageToItem:   ['images.write', 'write.item'],
  importDocumentToItem: ['documents.write', 'write.item'],
  deleteDocumentFromItem: ['documents.write', 'write.item'],
  listDocumentNames:   ['documents.read', 'read.item'],
  listDocumentDates:   ['documents.read', 'read.item'],
  listDocumentSizes:   ['documents.read', 'read.item'],
  createQrCode:        ['images.write'],
  recreateSvgThumbnail: ['images.write'],
  triggerCanvasThumbnail: ['images.write'],
  createCanvasScreenshot: ['images.read'],
  shareAsset:          ['share.invoke'],
  saveStringToFile:    ['files.writeTemp'],
  savePdf:             ['files.writeTemp'],
  exportInventoryReport: ['files.writeTemp'],
  queryCanvasItems:    ['read.canvas'],
  moveCanvasItems:     ['write.canvas'],
  resizeCanvasItems:   ['write.canvas'],
  alignCanvasItems:    ['write.canvas'],
  autoArrangeCanvasItems: ['write.canvas'],
  animateItems:        ['write.canvas'],
  cancelAnimation:     ['write.canvas'],
  scheduleRepeating:   ['write.canvas'],
  scheduleOnce:        ['write.canvas'],
  cancelSchedule:      ['write.canvas'],
  mutateGraph:         ['write.graph'],
  changeCanvasLevel:   ['write.canvas'],
  launchAiChat:        ['ai.invoke'],
  writeClipboardText:  ['clipboard.write'],
  addDurationToIso:    [],
  diffIsoDays:         [],
  compareIso:          [],
  formatIso:           [],
  getDateTimePart:     [],
  createDateTimeFromParts: [],
  setDateTimePart:     [],
  getCurrentWeekdayIndex: [],
  getCurrentWeekdayName: [],
  getLastWeekdayBefore: [],
  getNextWeekdayAfter: [],
  convertQuantity:     [],
  listUnits:           [],
  listConvertibleUnits: [],
  requestDisplayRefresh: ['write.item'],
  setElementType:      ['write.item'],
  applyElementDefaults: ['write.item'],
  logScript:           ['diagnostics.log'],
};

/** Extracts the first string-literal method name from callHost() invocations in Blockly code. */
const CALL_HOST_LITERAL_RE = /callHost\s*\(\s*['"]([^'"]+)['"]/g;

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Validates semantic integrity of a template document.
 * Runs structural validation first; returns structural errors if invalid.
 * Then checks cross-reference consistency.
 * Returns a ValidationResult. Does not throw.
 */
export function validateSemantics(document: unknown): ValidationResult {
  try {
    const structuralResult = validateStructure(document);
    if (!structuralResult.valid) {
      return structuralResult;
    }

    const doc = document as TemplateDocument;
    const lookups = buildSemanticLookups(doc);

    return mergeResults(
      checkA_primaryElement(doc, lookups),
      checkElementTreeCompanionChrome(doc),
      checkElementTemplatePickerCompanionChrome(doc),
      checkElementCanvasEnterViewport(doc),
      checkElementLink2LabelPreferred(doc),
      checkElementOverview(doc),
      checkElementEditForms(doc),
      checkB_elementCategories(doc, lookups),
      checkC_propertyCategories(doc, lookups),
      checkF_scriptReferences(doc, lookups),
      checkScriptCapabilityDeclarations(doc, lookups),
      checkJ_fieldBindings(doc, lookups),
      checkK_expressionDependencyCycles(doc),
      checkL_itemVariableKeysInExpressions(doc),
      validateAnimationDefinitions(doc.animations),
      checkComponents(doc, lookups),
      checkM_templateClasses(doc, lookups),
    );
  } catch {
    return makeFailResult('schema.invalid_root', 'Unexpected error during semantic validation');
  }
}

// ── Check A: Primary element reference ───────────────────────────────────────

function checkA_primaryElement(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  const { primaryElementId } = doc.metadata;
  // Generic list-root sentinel: no authored container element required (DEC-050).
  if (primaryElementId === 'default') {
    return makePassResult();
  }
  if (!lookups.elementById.has(primaryElementId)) {
    return addIssue(makePassResult(), {
      severity: 'error',
      code: 'semantic.missing_primary_element',
      message: `Primary element id '${primaryElementId}' does not reference any element in this document`,
      path: 'metadata.primaryElementId',
    });
  }
  return makePassResult();
}

function checkElementTreeCompanionChrome(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    if (element.hideForUser && element.alwaysShowTreeCompanionPanel) {
      result = addIssue(result, {
        severity: 'warning',
        code: 'semantic.hidden_element_tree_companion',
        message: `Element '${element.id}' has hideForUser and alwaysShowTreeCompanionPanel; hidden elements are not user-pickable but may still seed a tree panel on enter of their canvas`,
        path: `elements[${i}].alwaysShowTreeCompanionPanel`,
      });
    }
  }
  return result;
}

function checkElementTemplatePickerCompanionChrome(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    if (element.hideForUser && element.alwaysShowTemplatePickerCompanionPanel) {
      result = addIssue(result, {
        severity: 'warning',
        code: 'semantic.hidden_element_template_picker_companion',
        message: `Element '${element.id}' has hideForUser and alwaysShowTemplatePickerCompanionPanel; hidden elements are not user-pickable but may still seed an item side panel on enter of their canvas`,
        path: `elements[${i}].alwaysShowTemplatePickerCompanionPanel`,
      });
    }
  }
  return result;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function checkElementCanvasEnterViewport(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const spec = doc.elements[i]?.canvasEnterViewport;
    if (spec === undefined) {
      continue;
    }
    const path = `elements[${i}].canvasEnterViewport`;
    if (spec === null || typeof spec !== 'object') {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_canvas_enter_viewport',
        message: `Element '${doc.elements[i].id}' canvasEnterViewport must be an object with x, y, and exactly one of width or height`,
        path,
      });
      continue;
    }
    const record = spec as { x?: unknown; y?: unknown; width?: unknown; height?: unknown };
    if (!isFiniteNumber(record.x) || !isFiniteNumber(record.y)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_canvas_enter_viewport',
        message: `Element '${doc.elements[i].id}' canvasEnterViewport requires finite x and y`,
        path,
      });
    }
    const hasWidth = record.width !== undefined;
    const hasHeight = record.height !== undefined;
    if (hasWidth === hasHeight) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_canvas_enter_viewport',
        message: `Element '${doc.elements[i].id}' canvasEnterViewport must set exactly one of width or height`,
        path,
      });
    }
    if (hasWidth && (!isFiniteNumber(record.width) || record.width <= 0)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_canvas_enter_viewport',
        message: `Element '${doc.elements[i].id}' canvasEnterViewport.width must be a finite number greater than 0`,
        path: `${path}.width`,
      });
    }
    if (hasHeight && (!isFiniteNumber(record.height) || record.height <= 0)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_canvas_enter_viewport',
        message: `Element '${doc.elements[i].id}' canvasEnterViewport.height must be a finite number greater than 0`,
        path: `${path}.height`,
      });
    }
  }
  return result;
}

const LINK2LABEL_HANDOFF_KINDS = new Set(['deeplink', 'exif-image', 'plain-image']);

function checkElementLink2LabelPreferred(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    const preferred = element.link2LabelPreferred;
    if (preferred === undefined) {
      continue;
    }
    if (preferred === null || typeof preferred !== 'object' || Array.isArray(preferred)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_link2label_preferred',
        message: `Element '${element.id}' link2LabelPreferred must be an object`,
        path: `elements[${i}].link2LabelPreferred`,
      });
      continue;
    }
    if (
      preferred.handoffKind !== undefined &&
      !LINK2LABEL_HANDOFF_KINDS.has(preferred.handoffKind)
    ) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_link2label_handoff_kind',
        message: `Element '${element.id}' link2LabelPreferred.handoffKind must be deeplink, exif-image, or plain-image`,
        path: `elements[${i}].link2LabelPreferred.handoffKind`,
      });
    }
    if (
      preferred.templateId !== undefined &&
      (typeof preferred.templateId !== 'string' || preferred.templateId.trim() === '')
    ) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_link2label_template_id',
        message: `Element '${element.id}' link2LabelPreferred.templateId must be a non-empty string when present`,
        path: `elements[${i}].link2LabelPreferred.templateId`,
      });
    }
    if (
      preferred.includeContent !== undefined &&
      typeof preferred.includeContent !== 'boolean'
    ) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_link2label_include_content',
        message: `Element '${element.id}' link2LabelPreferred.includeContent must be a boolean when present`,
        path: `elements[${i}].link2LabelPreferred.includeContent`,
      });
    }
  }
  return result;
}

// ── Check B: Element category references ─────────────────────────────────────

function checkB_elementCategories(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    for (let j = 0; j < element.categoryIds.length; j++) {
      const categoryId = element.categoryIds[j];
      if (!lookups.categoryById.has(categoryId)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.missing_category',
          message: `Element '${element.id}' references category '${categoryId}' which does not exist in any category bundle`,
          path: `elements[${i}].categoryIds[${j}]`,
        });
      }
    }
  }
  return result;
}

// ── Check C: Property category back-references ───────────────────────────────

function checkC_propertyCategories(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  for (let bundleIdx = 0; bundleIdx < doc.categories.length; bundleIdx++) {
    const bundle = doc.categories[bundleIdx];
    for (let propIdx = 0; propIdx < bundle.properties.length; propIdx++) {
      const property = bundle.properties[propIdx];
      if (property.categoryId === null || !lookups.categoryById.has(property.categoryId)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.missing_property',
          message: `Property '${property.id}' has categoryId '${property.categoryId}' which does not reference a known category definition`,
          path: `categories[${bundleIdx}].properties[${propIdx}].categoryId`,
        });
      }
    }
  }
  return result;
}

function validateEventHandlerBinding(
  binding: ScriptEventBinding,
  ownerId: string,
  pathPrefix: string,
  lookups: SemanticLookups,
  ownerNoun: 'Class' | 'Element' = 'Element',
): ValidationResult {
  const pseudoComponent = { id: ownerId } as ComponentNode;
  let result = validatePressHandlerReference(binding.handler, {
    elementId: ownerId,
    path: `${pathPrefix}.handler`,
    label: `Event binding '${binding.id}'`,
    lookups,
  });

  for (const [index, watchRef] of (binding.watchRefs ?? []).entries()) {
    if (watchRef.kind !== 'itemField' && watchRef.kind !== 'categoryProperty') {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_data_ref',
        message: `Event binding '${binding.id}' watchRefs[${index}] must be itemField or categoryProperty`,
        path: `${pathPrefix}.watchRefs[${index}]`,
      });
      continue;
    }
    result = mergeResults(
      result,
      checkDataRef(
        pseudoComponent,
        watchRef,
        `${pathPrefix}.watchRefs[${index}]`,
        lookups,
        ownerNoun,
      ),
    );
  }

  return result;
}

// ── Check F: Script references ────────────────────────────────────────────────

function checkF_scriptReferences(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];

    // Event handler bindings (script id or action id)
    const events = element.scripts?.events ?? [];
    for (let j = 0; j < events.length; j++) {
      result = mergeResults(
        result,
        validateEventHandlerBinding(
          events[j],
          element.id,
          `elements[${i}].scripts.events[${j}]`,
          lookups,
        ),
      );
    }

    // Explicit item script API expose list (script handlers only in v1)
    const apiHandlers = element.scripts?.api ?? [];
    for (let j = 0; j < apiHandlers.length; j++) {
      const pathPrefix = `elements[${i}].scripts.api[${j}]`;
      const normalized = normalizeEventHandler(apiHandlers[j]);
      if (!normalized || normalized.kind !== 'script') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_event_handler',
          message:
            `Element '${element.id}' scripts.api[${j}] must be a script handler ` +
            `(kind 'script' with scriptId, or legacy { scriptId }).`,
          path: pathPrefix,
        });
        continue;
      }
      result = mergeResults(
        result,
        validatePressHandlerReference(normalized, {
          elementId: element.id,
          path: pathPrefix,
          label: `Element '${element.id}' scripts.api[${j}]`,
          lookups,
        }),
      );
    }

    // Computed values backed by a script
    const computedValues = element.scripts?.computedValues ?? [];
    for (let j = 0; j < computedValues.length; j++) {
      const computed = computedValues[j];
      if (computed.type === 'script' && computed.script != null) {
        const scriptId = computed.script.scriptId;
        if (!lookups.scriptById.has(scriptId)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_script_reference',
            message: `Computed value '${computed.id}' references script '${scriptId}' which is not defined in document.scripts`,
            path: `elements[${i}].scripts.computedValues[${j}].script.scriptId`,
          });
        }
      }
    }

    // Actions backed by a script invocation (WP-041)
    const actions = element.scripts?.actions ?? [];
    for (let j = 0; j < actions.length; j++) {
      const action = actions[j];
      if (action.type === 'script' && action.script != null) {
        const scriptId = action.script.scriptId;
        if (!lookups.scriptById.has(scriptId)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_script_reference',
            message: `Action '${action.id}' references script '${scriptId}' which is not defined in document.scripts`,
            path: `elements[${i}].scripts.actions[${j}].script.scriptId`,
          });
        }
      }
    }

    // Layout-scoped script event bindings (WP-046)
    for (let k = 0; k < element.layouts.length; k++) {
      const layout = element.layouts[k];
      const layoutEvents = layout.scripts?.events ?? [];
      for (let j = 0; j < layoutEvents.length; j++) {
        result = mergeResults(
          result,
          validateEventHandlerBinding(
            layoutEvents[j],
            element.id,
            `elements[${i}].layouts[${k}].scripts.events[${j}]`,
            lookups,
          ),
        );
      }
    }
  }
  return result;
}

// ── Check G: Blockly script capability declarations (WP-041) ───────────────────

type BlocklyCapabilityScanTarget = {
  readonly script: BlocklyScriptDefinition;
  /** Path prefix without trailing field (e.g. scripts.scripts[0] or classes[0].library.scripts[1]). */
  readonly pathPrefix: string;
};

/**
 * Collects every blockly-js script from document.scripts and classes[].library,
 * deduped by script id (document.scripts first so dual-exported ids are checked once).
 */
function collectBlocklyScriptsForCapabilityCheck(
  doc: TemplateDocument,
): BlocklyCapabilityScanTarget[] {
  const seenIds = new Set<string>();
  const targets: BlocklyCapabilityScanTarget[] = [];

  const scripts = doc.scripts?.scripts ?? [];
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (script.language !== 'blockly-js') continue;
    if (seenIds.has(script.id)) continue;
    seenIds.add(script.id);
    targets.push({
      script: script as BlocklyScriptDefinition,
      pathPrefix: `scripts.scripts[${i}]`,
    });
  }

  const classes = doc.classes ?? [];
  for (let c = 0; c < classes.length; c++) {
    const libScripts = classes[c].library?.scripts ?? [];
    for (let j = 0; j < libScripts.length; j++) {
      const script = libScripts[j];
      if (script.language !== 'blockly-js') continue;
      if (seenIds.has(script.id)) continue;
      seenIds.add(script.id);
      targets.push({
        script: script as BlocklyScriptDefinition,
        pathPrefix: `classes[${c}].library.scripts[${j}]`,
      });
    }
  }

  return targets;
}

/**
 * For each Blockly script in document.scripts.scripts and classes[].library.scripts,
 * scan the code for callHost() calls and verify that each referenced host method's
 * required capabilities are listed in the script's permissions array.
 * Uses static regex analysis on string-literal arguments; dynamic callHost() calls
 * are not flagged. Script ids seen in both bags are checked once (document path wins).
 */
function checkScriptCapabilityDeclarations(
  doc: TemplateDocument,
  _lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  for (const { script: blocklyScript, pathPrefix } of collectBlocklyScriptsForCapabilityCheck(doc)) {
    const declaredCapabilities = new Set<string>(
      blocklyScript.permissions.map((p) => p.capability),
    );

    // Reset global regex state before each script
    CALL_HOST_LITERAL_RE.lastIndex = 0;
    const checkedMethods = new Set<string>();
    let match: RegExpExecArray | null;

    while ((match = CALL_HOST_LITERAL_RE.exec(blocklyScript.code)) !== null) {
      const method = match[1];
      if (checkedMethods.has(method)) continue;
      checkedMethods.add(method);

      const required = BLOCKLY_METHOD_CAPABILITIES[method];
      if (required === undefined) {
        // Unknown method is publish-invalid: host capability contract is frozen.
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.script_undeclared_capability',
          message: `Blockly script '${blocklyScript.id}' calls unknown host method '${method}'.`,
          path: `${pathPrefix}.code`,
        });
        continue;
      }

      for (const cap of required) {
        if (!declaredCapabilities.has(cap)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.script_undeclared_capability',
            message: `Blockly script '${blocklyScript.id}' calls host method '${method}' but does not declare the required capability '${cap}' in permissions.`,
            path: `${pathPrefix}.permissions`,
          });
        }
      }
    }
  }
  return result;
}

// ── Check J: Field expression bindings (DEC-042) ─────────────────────────────

function checkJ_fieldBindings(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  const expressions = doc.scripts?.expressions ?? [];
  const pseudoComponent = { id: 'field-binding' } as ComponentNode;

  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    const bindings = element.scripts?.fieldBindings?.bindings ?? [];

    for (let j = 0; j < bindings.length; j++) {
      const binding = bindings[j];
      const pathPrefix = `elements[${i}].scripts.fieldBindings.bindings[${j}]`;

      result = mergeResults(
        result,
        checkDataRef(pseudoComponent, binding.target, `${pathPrefix}.target`, lookups),
      );

      if (binding.target.kind === 'itemMetric' || binding.target.kind === 'themeColor' || binding.target.kind === 'itemVariable' || binding.target.kind === 'localState' || isItemMembershipRef(binding.target)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message:
            `Field binding '${binding.id}' cannot target ${binding.target.kind}; ` +
            'use itemField or categoryProperty',
          path: `${pathPrefix}.target`,
        });
      }

      if (binding.value.kind === 'expression') {
        const expressionId = binding.value.expressionId;
        if (!lookups.expressionById.has(expressionId)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_expression_reference',
            message: `Field binding '${binding.id}' references expression '${expressionId}' which is not defined`,
            path: `${pathPrefix}.value.expressionId`,
          });
        } else {
          const root = expressions.find((e) => e.id === expressionId)?.root as
            | Record<string, unknown>
            | undefined;
          for (const metric of collectItemMetricRefsFromAstRoot(root)) {
            if (!ITEM_METRIC_KEYS.has(metric)) {
              result = addIssue(result, {
                severity: 'error',
                code: 'semantic.invalid_data_ref',
                message: `Field binding '${binding.id}' references unknown item metric '${metric}'`,
                path: `${pathPrefix}.value.expressionId`,
              });
            }
          }
          for (const variableKey of collectItemVariableKeysFromAstRoot(root)) {
            result = mergeResults(
              result,
              validateItemVariableKey(`Field binding '${binding.id}'`, variableKey, `${pathPrefix}.value.expressionId`),
            );
          }
        }
      }
    }

    for (const cycle of detectFieldBindingCycles(bindings, expressions)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.field_binding_cycle',
        message: cycle.message,
        path: `elements[${i}].scripts.fieldBindings`,
      });
    }
  }

  return result;
}

// ── Check K: Expression library dependency cycles ─────────────────────────────

function checkK_expressionDependencyCycles(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  const expressions = doc.scripts?.expressions ?? [];

  for (const cycle of detectExpressionCycles(expressions)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.expression_cycle',
      message: cycle.message,
      path: 'scripts.expressions',
    });
  }

  return result;
}

function validateItemVariableKey(
  label: string,
  key: string,
  path: string,
): ValidationResult {
  if (!key.trim()) {
    return addIssue(makePassResult(), {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: `${label} itemVariable key must not be empty`,
      path,
    });
  }
  if (key.length > ITEM_VARIABLE_POLICY.maxKeyLength || !ITEM_VARIABLE_POLICY.keyPattern.test(key)) {
    return addIssue(makePassResult(), {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: `${label} itemVariable key '${key}' is invalid`,
      path,
    });
  }
  return makePassResult();
}

function validateLocalStateKeyRef(
  label: string,
  key: string,
  path: string,
): ValidationResult {
  const check = validateLocalStateKey(key);
  if (!check.ok) {
    return addIssue(makePassResult(), {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: `${label} localState key is invalid (${check.message})`,
      path,
    });
  }
  if (key.length > LOCAL_STATE_POLICY.maxKeyLength) {
    return addIssue(makePassResult(), {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: `${label} localState key '${key}' is invalid`,
      path,
    });
  }
  return makePassResult();
}

function checkL_itemVariableKeysInExpressions(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();
  const expressions = doc.scripts?.expressions ?? [];

  for (let i = 0; i < expressions.length; i++) {
    const expression = expressions[i];
    const root = expression.root as Record<string, unknown> | undefined;
    for (const key of collectItemVariableKeysFromAstRoot(root)) {
      result = mergeResults(
        result,
        validateItemVariableKey(`Expression '${expression.id}'`, key, `scripts.expressions[${i}].root`),
      );
    }
  }

  return result;
}

// ── Check D/E/G/H/I: Component-level checks ────────────────────────────────────────────

function checkComponents(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();

  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];

    // Per-element computed value IDs for expression resolution (check G)
    const elementComputedIds = new Set<string>(
      (element.scripts?.computedValues ?? []).map(cv => cv.id),
    );

    // Track component IDs to detect cross-layout duplicates (check I)
    const seenIds = new Map<string, number>();

    walkComponents(element, i, (component, path) => {
      // Check D: DataRef in binding fields
      if (component.binding?.read != null) {
        result = mergeResults(
          result,
          checkDataRef(component, component.binding.read, `${path}.binding.read`, lookups),
        );
        if (component.binding.read.kind === 'itemMetric') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use itemMetric on binding.read; ` +
              'metrics are display-only — bind via typed source (e.g. number.source)',
            path: `${path}.binding.read`,
          });
        }
        if (component.binding.read.kind === 'themeColor') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use themeColor on binding.read; ` +
              'theme colors are display-only — bind via typed source',
            path: `${path}.binding.read`,
          });
        }
        if (isItemMembershipRef(component.binding.read)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use ${component.binding.read.kind} on binding.read; ` +
              'item membership checks are display-only — bind via typed source or visibility',
            path: `${path}.binding.read`,
          });
        }
      }
      if (component.binding?.write != null) {
        result = mergeResults(
          result,
          checkDataRef(component, component.binding.write, `${path}.binding.write`, lookups),
        );
        if (
          component.binding.write.kind === 'categoryProperty'
          && component.binding.write.itemFrom != null
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot write through categoryProperty.itemFrom; `
              + 'mutate the related item via host APIs (itemId)',
            path: `${path}.binding.write.itemFrom`,
          });
        }
        if (component.binding.write.kind === 'itemMetric') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use itemMetric on binding.write; ` +
              'metrics are read-only and cannot be written back',
            path: `${path}.binding.write`,
          });
        }
        if (component.binding.write.kind === 'themeColor') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use themeColor on binding.write; ` +
              'theme colors are read-only and cannot be written back',
            path: `${path}.binding.write`,
          });
        }
        if (isItemMembershipRef(component.binding.write)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message:
              `Component '${component.id}' cannot use ${component.binding.write.kind} on binding.write; ` +
              'item membership checks are read-only and cannot be written back',
            path: `${path}.binding.write`,
          });
        }
      }

      // Check D/G/H: Type-specific source fields
      result = mergeResults(
        result,
        checkTypedSources(component, path, element, elementComputedIds, lookups),
      );

      // Check E: Action references
      result = mergeResults(result, checkActionRefs(component, path, element, lookups));

      result = mergeResults(result, checkBindingContract(component, path));

      // Check I: accumulate component ID occurrences
      seenIds.set(component.id, (seenIds.get(component.id) ?? 0) + 1);
    });

    // Report duplicate IDs found across this element's layouts
    for (const [componentId, count] of seenIds) {
      if (count > 1) {
        result = addIssue(result, {
          severity: 'warning',
          code: 'semantic.orphaned_component',
          message: `Component id '${componentId}' appears more than once across layouts in element '${element.id}' (compile uses element+layout scoped keys)`,
          path: `elements[${i}]`,
        });
      }
    }
  }

  return result;
}

// ── Check D: Single DataRef validation ───────────────────────────────────────

function checkDataRef(
  component: ComponentNode,
  ref: DataRef,
  path: string,
  lookups: SemanticLookups,
  ownerNoun: string = 'Component',
): ValidationResult {
  const ownerLabel = `${ownerNoun} '${component.id}'`;
  switch (ref.kind) {
    case 'indexedItemField': {
      if (!Number.isInteger(ref.index) || ref.index < 0) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: ownerLabel + " has invalid image slot index '" + String(ref.index) + "'; expected a non-negative integer",
          path,
        });
      }
      break;
    }

    case 'categoryProperty': {
      const catExists = lookups.categoryById.has(ref.categoryId);
      const prop = lookups.propertyById.get(ref.propertyId);
      const propValid = catExists && prop != null && prop.categoryId === ref.categoryId;
      if (!catExists || !propValid) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_property_reference',
          message: `${ownerLabel} references unknown category-property '${ref.categoryId}.${ref.propertyId}'`,
          path,
        });
      }
      if (ref.itemFrom != null) {
        if (ref.itemFrom.kind !== 'itemVariable') {
          return addIssue(makePassResult(), {
            severity: 'error',
            code: 'semantic.invalid_data_ref',
            message: `${ownerLabel} categoryProperty.itemFrom must use kind 'itemVariable'`,
            path: `${path}.itemFrom`,
          });
        }
        const keyResult = validateItemVariableKey(
          ownerLabel + ' categoryProperty.itemFrom',
          ref.itemFrom.key,
          `${path}.itemFrom.key`,
        );
        if (!keyResult.valid) {
          return keyResult;
        }
        // DEC-074: foreign layout binds need related-item load (read.relatives).
        // Layout paint prefetches without a script permissions bag; gated contexts
        // (scripts) must declare read.relatives. Advisory only — do not fail publish.
        return addIssue(makePassResult(), {
          severity: 'info',
          code: 'policy.capability_not_allowed',
          message:
            `${ownerLabel} uses categoryProperty.itemFrom '${ref.itemFrom.key}' `
            + '(DEC-074). Cross-item layout resolve requires related-item fetch '
            + '(read.relatives when BindingContext.capabilities is set).',
          path: `${path}.itemFrom`,
        });
      }
      break;
    }
    case 'computed': {
      if (!lookups.computedValueIds.has(ref.computedId)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references computed value '${ref.computedId}' which is not defined`,
          path,
        });
      }
      break;
    }
    case 'runtimeContext': {
      if (!RUNTIME_CONTEXT_KEYS.has(ref.key)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references unknown runtime context key '${ref.key}'`,
          path,
        });
      }
      break;
    }
    case 'itemMetric': {
      if (!ITEM_METRIC_KEYS.has(ref.metric)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references unknown item metric '${ref.metric}'`,
          path,
        });
      }
      break;
    }
    case 'itemVariable': {
      return validateItemVariableKey(ownerLabel, ref.key, path);
    }
    case 'localState': {
      return validateLocalStateKeyRef(ownerLabel, ref.key, path);
    }
    case 'themeColor': {
      if (!THEME_COLOR_KEY_SET.has(ref.key)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references unknown theme color key '${ref.key}'`,
          path,
        });
      }
      if (ref.mode != null && ref.mode !== 'auto' && ref.mode !== 'light' && ref.mode !== 'dark') {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} has invalid themeColor mode '${String(ref.mode)}'`,
          path,
        });
      }
      break;
    }
    case 'itemHasClass': {
      const classId = typeof ref.classId === 'string' ? ref.classId.trim() : '';
      if (!classId) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} itemHasClass.classId must be a non-empty string`,
          path,
        });
      }
      if (!lookups.classById.has(classId)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references unknown class '${classId}'`,
          path,
        });
      }
      break;
    }
    case 'itemHasCategory': {
      const categoryId = typeof ref.categoryId === 'string' ? ref.categoryId.trim() : '';
      if (!categoryId) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} itemHasCategory.categoryId must be a non-empty string`,
          path,
        });
      }
      if (!lookups.categoryById.has(categoryId)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_property_reference',
          message: `${ownerLabel} references unknown category '${categoryId}'`,
          path,
        });
      }
      break;
    }
    case 'itemHasElement': {
      const elementId = typeof ref.elementId === 'string' ? ref.elementId.trim() : '';
      if (!elementId) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} itemHasElement.elementId must be a non-empty string`,
          path,
        });
      }
      if (!lookups.elementById.has(elementId)) {
        return addIssue(makePassResult(), {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: `${ownerLabel} references unknown element '${elementId}'`,
          path,
        });
      }
      break;
    }
    // 'itemField': no cross-reference validation at this layer
  }
  return makePassResult();
}

// ── Check D/G/H: Type-specific source fields ──────────────────────────────────

function checkTypedSources(
  component: ComponentNode,
  path: string,
  element: ElementDefinition,
  elementComputedIds: Set<string>,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();

  // Use a loose cast so we can safely access type-specific props with optional
  // chaining. Nodes that rely purely on `binding:` may omit the type-specific
  // field (e.g. no `text:` on a type='text' node), which would otherwise throw
  // a TypeError here before the structural validator has a chance to flag it.
  // `checkValueSource` already handles undefined/null gracefully.
  const n = component as unknown as Record<string, Record<string, unknown> | undefined>;

  switch (component.type) {
    case 'text':
      result = mergeResults(
        result,
        checkValueSource(component, n.text?.source, `${path}.text.source`, element, elementComputedIds, lookups),
        checkValueSource(component, n.text?.href, `${path}.text.href`, element, elementComputedIds, lookups),
      );
      break;
    case 'number':
      result = mergeResults(
        result,
        checkValueSource(component, n.number?.source, `${path}.number.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'boolean':
      result = mergeResults(
        result,
        checkValueSource(component, n.boolean?.source, `${path}.boolean.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'date':
      result = mergeResults(
        result,
        checkValueSource(component, n.date?.source, `${path}.date.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'enum':
      result = mergeResults(
        result,
        checkValueSource(component, n.enum?.source, `${path}.enum.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'stringSelect':
      result = mergeResults(
        result,
        checkValueSource(component, n.stringSelect?.source, `${path}.stringSelect.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'listView':
      result = mergeResults(
        result,
        checkValueSource(component, n.listView?.source, `${path}.listView.source`, element, elementComputedIds, lookups),
      );
      result = mergeResults(
        result,
        checkValueSource(
          component,
          n.listView?.selectionSource,
          `${path}.listView.selectionSource`,
          element,
          elementComputedIds,
          lookups,
        ),
      );
      break;
    case 'image': {
      const imgSrc = n.image?.source as Record<string, unknown> | undefined;
      if (imgSrc != null) {
        if (imgSrc.kind === 'manifest') {
          // Check H: manifest asset reference
          if (!lookups.assetEntryById.has(imgSrc.assetId as string)) {
            result = addIssue(result, {
              severity: 'error',
              code: 'semantic.invalid_asset_reference',
              message: `Asset reference '${imgSrc.assetId}' is not found in document.assets`,
              path: `${path}.image.source.assetId`,
            });
          }
        } else if (
          imgSrc.kind === 'static' ||
          imgSrc.kind === 'binding' ||
          imgSrc.kind === 'expression'
        ) {
          // Check D/G: ValueSource variant
          result = mergeResults(
            result,
            checkValueSource(component, imgSrc, `${path}.image.source`, element, elementComputedIds, lookups),
          );
        }
        // 'inline' and 'relativePath' AssetRef kinds: no cross-reference check needed
      }
      result = mergeResults(
        result,
        checkValueSource(component, n.image?.href, `${path}.image.href`, element, elementComputedIds, lookups),
      );
      break;
    }
    case 'html': {
      result = mergeResults(
        result,
        checkValueSource(component, n.html?.source, `${path}.html.source`, element, elementComputedIds, lookups),
      );
      break;
    }
    case 'svg': {
      const svgSrc = n.svg?.source as Record<string, unknown> | undefined;
      if (svgSrc == null) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `Svg component '${component.id}' requires svg.source`,
          path: `${path}.svg.source`,
        });
      } else if (
        svgSrc.kind === 'static' ||
        svgSrc.kind === 'binding' ||
        svgSrc.kind === 'expression'
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Svg component '${component.id}' source must be an AssetRef, not ValueSource`,
          path: `${path}.svg.source`,
        });
      } else {
        if (svgSrc.kind === 'manifest') {
          if (!lookups.assetEntryById.has(svgSrc.assetId as string)) {
            result = addIssue(result, {
              severity: 'error',
              code: 'semantic.invalid_asset_reference',
              message: `Asset reference '${svgSrc.assetId}' is not found in document.assets`,
              path: `${path}.svg.source.assetId`,
            });
          } else {
            const asset = lookups.assetEntryById.get(svgSrc.assetId as string);
            const mediaType = asset?.mediaType as string | undefined;
            if (mediaType != null && mediaType !== 'image/svg+xml') {
              result = addIssue(result, {
                severity: 'warning',
                code: 'schema.invalid_field_type',
                message: `Svg component '${component.id}' manifest asset should use mediaType image/svg+xml (got '${mediaType}')`,
                path: `${path}.svg.source.assetId`,
              });
            }
          }
        }
        if (svgSrc.kind === 'inline') {
          const mediaType = svgSrc.mediaType as string | undefined;
          if (mediaType != null && mediaType !== 'image/svg+xml') {
            result = addIssue(result, {
              severity: 'warning',
              code: 'schema.invalid_field_type',
              message: `Svg component '${component.id}' inline asset should use mediaType image/svg+xml (got '${mediaType}')`,
              path: `${path}.svg.source.mediaType`,
            });
          }
        }
      }
      if (n.svg?.fill != null) {
        result = mergeResults(
          result,
          checkValueSource(component, n.svg.fill, `${path}.svg.fill`, element, elementComputedIds, lookups),
        );
      }
      if (n.svg?.stroke != null) {
        result = mergeResults(
          result,
          checkValueSource(component, n.svg.stroke, `${path}.svg.stroke`, element, elementComputedIds, lookups),
        );
      }
      if (n.svg?.strokeWidth != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            n.svg.strokeWidth,
            `${path}.svg.strokeWidth`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      break;
    }
    case 'qrCode': {
      const qr = n.qrCode;
      if (qr?.source == null) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `QrCode component '${component.id}' requires qrCode.source`,
          path: `${path}.qrCode.source`,
        });
      } else {
        result = mergeResults(
          result,
          checkValueSource(component, qr.source, `${path}.qrCode.source`, element, elementComputedIds, lookups),
        );
      }
      if (qr?.color != null) {
        result = mergeResults(
          result,
          checkValueSource(component, qr.color, `${path}.qrCode.color`, element, elementComputedIds, lookups),
        );
      }
      if (qr?.backgroundColor != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            qr.backgroundColor,
            `${path}.qrCode.backgroundColor`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      break;
    }
    case 'rating':
      result = mergeResults(
        result,
        checkValueSource(component, n.rating?.source, `${path}.rating.source`, element, elementComputedIds, lookups),
      );
      if (n.rating?.maxRating != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            n.rating.maxRating,
            `${path}.rating.maxRating`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      break;
    case 'table': {
      const table = n.table;
      if (table?.source != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            table.source,
            `${path}.table.source`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      if (table?.tableSource != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            table.tableSource,
            `${path}.table.tableSource`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      if (table?.source != null && table?.tableSource != null) {
        result = mergeResults(
          result,
          addIssue(makePassResult(), {
            severity: 'warning',
            code: 'schema.invalid_field_type',
            message:
              'Table has both source and tableSource; list-backed table.source wins at paint (DEC-077 dual-bind).',
            path: `${path}.table.source`,
          }),
        );
      }
      result = mergeResults(result, checkListBackedTableEditable(n, path));
      break;
    }
    case 'chart': {
      const chart = n.chart;
      if (chart?.source != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            chart.source,
            `${path}.chart.source`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      if (chart?.tableSource != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            chart.tableSource,
            `${path}.chart.tableSource`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      if (chart?.source != null && chart?.tableSource != null) {
        result = mergeResults(
          result,
          addIssue(makePassResult(), {
            severity: 'warning',
            code: 'schema.invalid_field_type',
            message:
              'Chart has both source and tableSource; list-backed chart.source wins at paint (DEC-078).',
            path: `${path}.chart.source`,
          }),
        );
      }
      if (chart?.chartType != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            chart.chartType,
            `${path}.chart.chartType`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      if (chart?.xSource != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            chart.xSource,
            `${path}.chart.xSource`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      break;
    }
    case 'color':
      result = mergeResults(
        result,
        checkValueSource(component, n.color?.source, `${path}.color.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'button':
      result = mergeResults(
        result,
        checkValueSource(component, n.button?.label, `${path}.button.label`, element, elementComputedIds, lookups),
        checkValueSource(component, n.button?.href, `${path}.button.href`, element, elementComputedIds, lookups),
      );
      break;
    case 'badge':
      result = mergeResults(
        result,
        checkValueSource(component, n.badge?.source, `${path}.badge.source`, element, elementComputedIds, lookups),
      );
      break;
    case 'progress': {
      const prog = n.progress;
      result = mergeResults(
        result,
        checkValueSource(component, prog?.value, `${path}.progress.value`, element, elementComputedIds, lookups),
      );
      if (prog?.max != null) {
        result = mergeResults(
          result,
          checkValueSource(component, prog.max, `${path}.progress.max`, element, elementComputedIds, lookups),
        );
      }
      if (prog?.min != null) {
        result = mergeResults(
          result,
          checkValueSource(component, prog.min, `${path}.progress.min`, element, elementComputedIds, lookups),
        );
      }
      break;
    }
    case 'timerDisplay': {
      const td = n.timerDisplay;
      if (td?.timerId != null) {
        result = mergeResults(
          result,
          checkValueSource(
            component,
            td.timerId,
            `${path}.timerDisplay.timerId`,
            element,
            elementComputedIds,
            lookups,
          ),
        );
      }
      break;
    }
    case 'slider': {
      const sl = n.slider;
      result = mergeResults(
        result,
        checkValueSource(component, sl?.source, `${path}.slider.source`, element, elementComputedIds, lookups),
      );
      if (sl?.max != null) {
        result = mergeResults(
          result,
          checkValueSource(component, sl.max, `${path}.slider.max`, element, elementComputedIds, lookups),
        );
      }
      if (sl?.min != null) {
        result = mergeResults(
          result,
          checkValueSource(component, sl.min, `${path}.slider.min`, element, elementComputedIds, lookups),
        );
      }
      break;
    }
    case 'line': {
      const line = n.line;
      if (line?.strokeWidth != null) {
        result = mergeResults(
          result,
          checkValueSource(component, line.strokeWidth, `${path}.line.strokeWidth`, element, elementComputedIds, lookups),
        );
      }
      if (line?.color != null) {
        result = mergeResults(
          result,
          checkValueSource(component, line.color, `${path}.line.color`, element, elementComputedIds, lookups),
        );
      }
      break;
    }
    // 'group', 'spacer', 'dragHandle', 'customToken': no ValueSource fields to check
  }

  return result;
}

// ── V2 binding contract (DEC-036) ─────────────────────────────────────────────

function checkBindingContract(
  component: ComponentNode,
  path: string,
): ValidationResult {
  let result = makePassResult();

  if (hasNestedButtonLabelSource(component)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_button_label_shape',
      message:
        `Component '${component.id}' has invalid button.label shape: use flat ValueSource ` +
        `({ kind, value|ref|expressionId }) on button.label, not button.label.source`,
      path: `${path}.button.label`,
    });
  }

  const readRef = component.binding?.read;
  if (readRef != null && typeof readRef === 'object' && 'kind' in readRef) {
    if (!isComponentEditable(component)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.binding_read_on_non_editable',
        message:
          `Component '${component.id}' declares binding.read but is not editable; ` +
          'remove binding.read and use typed source (e.g. text.source) for display-only binding',
        path: `${path}.binding.read`,
      });
    } else {
      const display = getDisplayValueSource(component);
      const sourceRef = bindingRefFromValueSource(display);
      if (sourceRef != null && !dataRefsEqual(readRef as DataRef, sourceRef)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.binding_read_source_mismatch',
          message:
            `Component '${component.id}' binding.read must match the binding ref on the typed ` +
            'display source when both are present on an editable component',
          path: `${path}.binding.read`,
        });
      }
    }
  }

  return result;
}

/**
 * List-backed localState / itemVariable tables are read-only preview surfaces;
 * authoring editable=true is ignored at paint time — warn at validate.
 */
function checkListBackedTableEditable(
  n: { table?: { source?: unknown; editable?: boolean } },
  path: string,
): ValidationResult {
  if (n.table?.editable !== true) return makePassResult();
  const source = n.table.source;
  if (source == null || typeof source !== 'object') return makePassResult();
  const vs = source as { kind?: string; ref?: { kind?: string } };
  if (vs.kind !== 'binding') return makePassResult();
  const refKind = vs.ref?.kind;
  if (refKind !== 'localState' && refKind !== 'itemVariable') return makePassResult();
  return addIssue(makePassResult(), {
    severity: 'warning',
    code: 'schema.invalid_field_type',
    message:
      'List-backed table binds (localState / itemVariable arrays) are read-only; editable is ignored. First row of the array is column headers.',
    path: `${path}.table.editable`,
  });
}

// ── Check G: ValueSource expression reference ─────────────────────────────────

/**
 * Checks a ValueSource for DataRef integrity (check D) and expression ID
 * resolution (check G). Takes `unknown` to accept any ValueSource<T> variant
 * without requiring generic inference at each call site.
 */
function checkValueSource(
  component: ComponentNode,
  vs: unknown,
  path: string,
  element: ElementDefinition,
  elementComputedIds: Set<string>,
  lookups: SemanticLookups,
): ValidationResult {
  if (vs == null || typeof vs !== 'object') {
    return makePassResult();
  }
  const v = vs as Record<string, unknown>;

  if (v.kind === 'binding' && v.ref != null) {
    // Check D: DataRef contained within a ValueSource binding
    return checkDataRef(component, v.ref as DataRef, path, lookups);
  }

  if (v.kind === 'expression' && typeof v.expressionId === 'string') {
    // Check G: Expression ID must resolve in document-level expressions or element computed values
    const expressionId = v.expressionId;
    if (!lookups.expressionById.has(expressionId) && !elementComputedIds.has(expressionId)) {
      return addIssue(makePassResult(), {
        severity: 'error',
        code: 'semantic.invalid_expression_reference',
        message: `Expression reference '${expressionId}' cannot be resolved in element '${element.id}'`,
        path,
      });
    }
  }

  return makePassResult();
}

// ── Check E: Action references in components ──────────────────────────────────

function checkActionRefs(
  component: ComponentNode,
  path: string,
  element: ElementDefinition,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();

  if (component.interactions?.onPress != null) {
    result = mergeResults(
      result,
      validatePressHandlerReference(component.interactions.onPress, {
        elementId: element.id,
        path: `${path}.interactions.onPress`,
        label: `Component '${component.id}' onPress`,
        lookups,
      }),
    );
  }

  if (component.interactions?.onLongPress != null) {
    result = mergeResults(
      result,
      validatePressHandlerReference(component.interactions.onLongPress, {
        elementId: element.id,
        path: `${path}.interactions.onLongPress`,
        label: `Component '${component.id}' onLongPress`,
        lookups,
      }),
    );
  }

  if (component.type === 'button' && component.button.action != null) {
    result = mergeResults(
      result,
      validatePressHandlerReference(component.button.action, {
        elementId: element.id,
        path: `${path}.button.action`,
        label: `Component '${component.id}' button press`,
        lookups,
      }),
    );
  }

  for (const [index, handler] of (component.interactions?.onPressHandlers ?? []).entries()) {
    result = mergeResults(
      result,
      validatePressHandlerReference(handler, {
        elementId: element.id,
        path: `${path}.interactions.onPressHandlers[${index}]`,
        label: `Component '${component.id}' onPressHandlers[${index}]`,
        lookups,
      }),
    );
  }

  if (component.interactions?.onChange != null) {
    result = mergeResults(
      result,
      validatePressHandlerReference(component.interactions.onChange, {
        elementId: element.id,
        path: `${path}.interactions.onChange`,
        label: `Component '${component.id}' onChange`,
        lookups,
      }),
    );
  }

  for (const [index, handler] of (component.interactions?.onChangeHandlers ?? []).entries()) {
    result = mergeResults(
      result,
      validatePressHandlerReference(handler, {
        elementId: element.id,
        path: `${path}.interactions.onChangeHandlers[${index}]`,
        label: `Component '${component.id}' onChangeHandlers[${index}]`,
        lookups,
      }),
    );
  }

  if (component.interactions?.onLoad != null) {
    result = mergeResults(
      result,
      validatePressHandlerReference(component.interactions.onLoad, {
        elementId: element.id,
        path: `${path}.interactions.onLoad`,
        label: `Component '${component.id}' onLoad`,
        lookups,
      }),
    );
  }

  const hasChangeHandler =
    component.interactions?.onChange != null
    || (component.interactions?.onChangeHandlers?.length ?? 0) > 0;
  if (hasChangeHandler && !isComponentEditable(component)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.change_on_non_editable',
      message:
        `Component '${component.id}' declares onChange handler(s) but is not editable`,
      path: `${path}.interactions.onChange`,
    });
  }

  return result;
}

// ── Check M: Template Classes (DEC-066) ───────────────────────────────────────

/**
 * JSON fingerprint of the canonical script fields that must match for a shared
 * id (document.scripts and/or multiple class libraries). Id is compared separately.
 */
function scriptLibraryMemberFingerprint(script: AnyScriptDefinition): string {
  return JSON.stringify({
    language: script.language,
    code: script.code,
    permissions: script.permissions,
    params: script.params ?? null,
    displayName: script.displayName ?? null,
  });
}

/**
 * JSON fingerprint of the canonical expression fields that must match for a
 * shared id (document.scripts and/or multiple class libraries).
 */
function expressionLibraryMemberFingerprint(expr: ExpressionDefinition): string {
  return JSON.stringify({
    language: expr.language,
    outputType: expr.outputType,
    displayName: expr.displayName ?? null,
    root: expr.root,
  });
}

type LibraryMemberOwner<T> = { owner: string; member: T };

function checkM_templateClasses(
  doc: TemplateDocument,
  lookups: SemanticLookups,
): ValidationResult {
  let result = makePassResult();
  const classes = doc.classes ?? [];
  if (classes.length === 0 && !doc.elements.some((el) => (el.classIds?.length ?? 0) > 0)) {
    return result;
  }

  const classById = new Map<string, TemplateClassDefinition>();
  const scriptIdOwners = new Map<string, LibraryMemberOwner<AnyScriptDefinition>>();
  const exprIdOwners = new Map<string, LibraryMemberOwner<ExpressionDefinition>>();

  for (const script of doc.scripts?.scripts ?? []) {
    scriptIdOwners.set(script.id, { owner: 'document.scripts', member: script });
  }
  for (const expr of doc.scripts?.expressions ?? []) {
    exprIdOwners.set(expr.id, { owner: 'document.scripts', member: expr });
  }

  for (let i = 0; i < classes.length; i++) {
    const cls = classes[i];
    const path = `classes[${i}]`;

    if (!cls.id || typeof cls.id !== 'string') {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_class',
        message: `Template class at ${path} is missing a string id`,
        path: `${path}.id`,
      });
      continue;
    }

    if (classById.has(cls.id)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.duplicate_class_id',
        message: `Duplicate template class id '${cls.id}'`,
        path: `${path}.id`,
      });
    } else {
      classById.set(cls.id, cls);
    }

    for (let j = 0; j < (cls.categoryIds ?? []).length; j++) {
      const categoryId = cls.categoryIds[j];
      if (!lookups.categoryById.has(categoryId)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.missing_category',
          message: `Class '${cls.id}' references category '${categoryId}' which does not exist`,
          path: `${path}.categoryIds[${j}]`,
        });
      }
    }

    for (const script of cls.library?.scripts ?? []) {
      const existing = scriptIdOwners.get(script.id);
      if (existing) {
        if (
          scriptLibraryMemberFingerprint(existing.member) !==
          scriptLibraryMemberFingerprint(script)
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.duplicate_script_id',
            message:
              `Script id '${script.id}' in class '${cls.id}' collides with ${existing.owner}`
              + ' (definitions differ)',
            path: `${path}.library.scripts`,
          });
        }
      } else {
        scriptIdOwners.set(script.id, { owner: `class:${cls.id}`, member: script });
      }
    }
    for (const expr of cls.library?.expressions ?? []) {
      const existing = exprIdOwners.get(expr.id);
      if (existing) {
        if (
          expressionLibraryMemberFingerprint(existing.member) !==
          expressionLibraryMemberFingerprint(expr)
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.duplicate_expression_id',
            message:
              `Expression id '${expr.id}' in class '${cls.id}' collides with ${existing.owner}`
              + ' (definitions differ)',
            path: `${path}.library.expressions`,
          });
        }
      } else {
        exprIdOwners.set(expr.id, { owner: `class:${cls.id}`, member: expr });
      }
    }

    for (let j = 0; j < (cls.bindings?.events ?? []).length; j++) {
      const binding = cls.bindings!.events![j];
      const bindingPath = `${path}.bindings.events[${j}]`;
      if (!isClassScriptEventName(binding.event)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.class_event_not_allowed',
          message: `Class '${cls.id}' binds disallowed event '${binding.event}'`,
          path: `${bindingPath}.event`,
        });
      }
      if (binding.classComposition !== undefined) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_composition',
          message: `Class '${cls.id}' binding '${binding.id}' must not set classComposition (element-only)`,
          path: `${bindingPath}.classComposition`,
        });
      }
      result = mergeResults(
        result,
        validateEventHandlerBinding(binding, cls.id, bindingPath, lookups, 'Class'),
      );
    }

    for (let j = 0; j < (cls.bindings?.api ?? []).length; j++) {
      const handler = cls.bindings!.api![j];
      const apiPath = `${path}.bindings.api[${j}]`;
      const normalized = normalizeEventHandler(handler);
      if (!normalized || normalized.kind !== 'script') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_event_handler',
          message: `Class '${cls.id}' api[${j}] must be a script handler`,
          path: apiPath,
        });
        continue;
      }
      result = mergeResults(
        result,
        validatePressHandlerReference(normalized, {
          elementId: cls.id,
          path: apiPath,
          label: `Class '${cls.id}' api[${j}]`,
          lookups,
        }),
      );
    }

    const seenVariableDefaultKeys = new Set<string>();
    for (let j = 0; j < (cls.variableDefaults ?? []).length; j++) {
      const row = cls.variableDefaults![j];
      const rowPath = `${path}.variableDefaults[${j}]`;
      const key = typeof row?.key === 'string' ? row.key.trim() : '';
      if (
        !key ||
        key.length > ITEM_VARIABLE_POLICY.maxKeyLength ||
        !ITEM_VARIABLE_POLICY.keyPattern.test(key)
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_variable_default',
          message: `Class '${cls.id}' variableDefaults[${j}] has an invalid key`,
          path: `${rowPath}.key`,
        });
      } else if (seenVariableDefaultKeys.has(key)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.duplicate_class_variable_default',
          message: `Class '${cls.id}' variableDefaults has duplicate key '${key}'`,
          path: `${rowPath}.key`,
        });
      } else {
        seenVariableDefaultKeys.add(key);
      }
      if (!isClassVariableDefaultType(row?.type ?? '')) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_variable_default',
          message: `Class '${cls.id}' variableDefaults[${j}] has a disallowed type '${String(row?.type)}'`,
          path: `${rowPath}.type`,
        });
      }
      const value = row?.value;
      let valueOk = false;
      if (row?.type === 'list') {
        valueOk = Array.isArray(value) && value.length === 0;
      } else {
        valueOk =
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          (value === null && row?.type === 'itemRef');
      }
      if (!valueOk) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_variable_default',
          message:
            row?.type === 'list'
              ? `Class '${cls.id}' variableDefaults[${j}] list value must be an empty array []`
              : `Class '${cls.id}' variableDefaults[${j}] value must be a scalar (or null for itemRef)`,
          path: `${rowPath}.value`,
        });
      }
    }
  }

  for (let i = 0; i < classes.length; i++) {
    const cls = classes[i];
    if (!cls.extendsClassId) {
      continue;
    }
    if (!classById.has(cls.extendsClassId)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.missing_class',
        message: `Class '${cls.id}' extends unknown class '${cls.extendsClassId}'`,
        path: `classes[${i}].extendsClassId`,
      });
      continue;
    }
    const ancestry = resolveClassAncestry(cls.id, classById);
    if (!ancestry) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.class_extends_cycle',
        message: `Class '${cls.id}' has a cyclic extendsClassId chain`,
        path: `classes[${i}].extendsClassId`,
      });
    }
  }

  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i];
    for (let j = 0; j < (element.classIds ?? []).length; j++) {
      const classId = element.classIds![j];
      if (!classById.has(classId)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.missing_class',
          message: `Element '${element.id}' references class '${classId}' which does not exist`,
          path: `elements[${i}].classIds[${j}]`,
        });
      }
    }
    for (let j = 0; j < (element.scripts?.events ?? []).length; j++) {
      const binding = element.scripts!.events![j];
      const mode = binding.classComposition;
      if (mode === undefined) {
        continue;
      }
      if (mode !== 'before' && mode !== 'after' && mode !== 'replace') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_composition',
          message: `Element '${element.id}' binding '${binding.id}' has invalid classComposition`,
          path: `elements[${i}].scripts.events[${j}].classComposition`,
        });
      }
      if (mode !== 'replace' && (binding.replaceRoleIds?.length ?? 0) > 0) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_class_composition',
          message: `Element '${element.id}' binding '${binding.id}' replaceRoleIds requires classComposition 'replace'`,
          path: `elements[${i}].scripts.events[${j}].replaceRoleIds`,
        });
      }
    }
  }

  return result;
}
